import React, { forwardRef } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';
import { useViewport } from '../../../hooks/useViewport';
import { group } from './platform';

/**
 * Pagination, the foot of a data table.
 *
 * CSS: `desktop/system.css` `.dsk-pagination`, `.dsk-pagination__pages`,
 * `.dsk-page-btn`, `.dsk-page-btn.is-current`.
 * Behaviour: none of the numbered reference behaviours.
 *
 * The app carries FOUR separate pagination implementations. This replaces all
 * of them, so it supports both shapes they use:
 *
 *   variant="numbered"  page buttons with an ellipsis once the run is long
 *   variant="simple"    prev / next only, with "Page 2 of 9" between them
 *
 * Left unset it resolves by track: numbered at 1024 and up, simple below, for
 * the reason set out on the branch itself.
 *
 * The current page is ink, per "a committed choice is ink", and also carries
 * `aria-current="page"` so the state is not colour-only.
 */

const ELLIPSIS = 'ellipsis';

/**
 * Build the page run. Always the first and last page, always `siblingCount`
 * pages either side of the current one, and an ellipsis wherever that skips
 * more than a single page (an ellipsis standing in for one page is longer than
 * the page it hides).
 */
export function buildPageRun(page, pageCount, siblingCount = 1) {
  if (pageCount <= 1) return pageCount === 1 ? [1] : [];

  const first = 1;
  const last = pageCount;
  const from = Math.max(first + 1, page - siblingCount);
  const to = Math.min(last - 1, page + siblingCount);

  const run = [first];

  if (from > first + 1) run.push(ELLIPSIS);
  else if (from === first + 1 && first + 1 <= last - 1) {
    /* No gap to elide. */
  }

  for (let value = from; value <= to; value += 1) run.push(value);

  if (to < last - 1) run.push(ELLIPSIS);
  if (last > first) run.push(last);

  return run;
}

export const Pagination = forwardRef(function Pagination(
  {
    page = 1,
    pageCount = 1,
    onPageChange,
    variant,
    siblingCount = 1,
    summary,
    totalItems,
    pageSize,
    itemNoun = 'results',
    label = 'Pagination',
    previousLabel = 'Previous page',
    nextLabel = 'Next page',
    className,
    ...rest
  },
  ref,
) {
  /* This component has no `ds-` twin, so it is mounted bare on the mobile track
     by /marketplace, /search, /collection and the card-detail listings — and
     `.dsk-pagination` is a no-wrap flex row whose page run cannot shrink below
     its content. Mid-run at page 5 of 20 the run is [1 … 4 5 6 … 20]: nine
     controls, and once they take the 44px touch target that is 396px of
     un-shrinkable buttons against a 358px column.

     So below the handoff the shape becomes the one the component already ships
     for narrow room — prev / "5 / 20" / next — and the summary sentence
     shortens to match. `Table` next door resolves its own track the same way,
     and it is the component that renders this one in its `pagination` slot.
     An explicit `variant` still wins on both tracks. */
  const { isDesktop } = useViewport();
  const resolvedVariant = variant || (isDesktop ? 'numbered' : 'simple');

  const goTo = (next) => {
    if (!onPageChange) return;
    const clamped = Math.min(Math.max(1, next), Math.max(1, pageCount));
    if (clamped !== page) onPageChange(clamped);
  };

  let summaryNode = summary;
  if (summaryNode == null && totalItems != null && pageSize) {
    const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(totalItems, page * pageSize);
    /* The sentence at 14px is ~200px wide and the phone has ~218px left beside
       the arrows, so it wrapped onto two ragged lines. The range says the same
       thing in a third of the room. */
    summaryNode = isDesktop
      ? `Showing ${group(from)} to ${group(to)} of ${group(totalItems)} ${itemNoun}`
      : `${group(from)}–${group(to)} of ${group(totalItems)} ${itemNoun}`;
  }
  if (summaryNode == null) summaryNode = `Page ${group(page)} of ${group(pageCount)}`;

  const run = resolvedVariant === 'numbered' ? buildPageRun(page, pageCount, siblingCount) : [];

  return (
    <nav ref={ref} className={cn('dsk-pagination', className)} aria-label={label} {...rest}>
      <span>{summaryNode}</span>

      <div className="dsk-pagination__pages">
        <button
          type="button"
          className="dsk-page-btn"
          onClick={() => goTo(page - 1)}
          disabled={page <= 1}
          aria-label={previousLabel}
        >
          <Icon as={CaretLeft} size="sm" />
        </button>

        {resolvedVariant === 'numbered'
          ? run.map((entry, index) =>
              entry === ELLIPSIS ? (
                // Presentational only. It wears `.dsk-page-btn` so it lands on
                // the same 32px baseline as the real buttons instead of
                // floating against the top of the flex row.
                <span key={`gap-${index}`} className="dsk-page-btn" aria-hidden="true">
                  &hellip;
                </span>
              ) : (
                <button
                  key={entry}
                  type="button"
                  className={cn('dsk-page-btn', entry === page && 'is-current')}
                  aria-current={entry === page ? 'page' : undefined}
                  onClick={() => goTo(entry)}
                >
                  {entry}
                </button>
              ),
            )
          : (
            <span className="dsk-page-btn" aria-live="polite">
              {`${group(page)} / ${group(pageCount)}`}
            </span>
          )}

        <button
          type="button"
          className="dsk-page-btn"
          onClick={() => goTo(page + 1)}
          disabled={page >= pageCount}
          aria-label={nextLabel}
        >
          <Icon as={CaretRight} size="sm" />
        </button>
      </div>
    </nav>
  );
});

export default Pagination;
